import React from 'react';
import { renderToString } from 'react-dom/server';
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import iso3166 from 'iso-3166-2';
import AutocompleteAddressItem from './AutocompleteAddressItem';

export const createGeocoder = (mapBoxAccessToken, placeHolder, locale) =>
  new MapboxGeocoder({
    accessToken: mapBoxAccessToken,
    minLength: 3,
    placeholder: placeHolder,
    limit: 7,
    language: locale,
    types: 'address',
    render: (item) =>
      // eslint-disable-next-line react/jsx-filename-extension
      renderToString(<AutocompleteAddressItem address={item.place_name} />),
  });

const getCity = (context) => {
  try {
    return context.find((elem) => elem.id.startsWith('place'))?.text || '';
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Error extracting City info', error);

    return '';
  }
};

const normalizeStr = (str) => {
  return str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
};

const getCountryAndRegion = async (context) => {
  try {
    let country = '';
    let region = '';
    let regionName = '';
    const regionInfo = context.find((elem) => elem.id.startsWith('region'));
    const placeInfo = context.find((elem) => elem.id.startsWith('place'));

    if (regionInfo) {
      country = regionInfo?.short_code.split('-')[0] || '';
      region = regionInfo?.short_code.split('-')[1] || '';
      regionName = regionInfo?.text || '';
    } else if (placeInfo) {
      country = placeInfo?.short_code.split('-')[0] || '';
      region = placeInfo?.short_code.split('-')[1] || '';
      regionName = placeInfo?.text || '';
    }

    if (
      country !== '' &&
      region !== '' &&
      !iso3166.subdivision(country, region)
    ) {
      const countryInfo = iso3166.country(country);
      let subdivisionInfo;
      if (regionName !== '') {
        subdivisionInfo = Object.entries(countryInfo.sub).find(
          (entry) => normalizeStr(entry[1].name) === normalizeStr(regionName)
        );
      }
      if (regionName === '' || !subdivisionInfo) {
        const { iso31662 } = await import('iso-3166');
        const subdivision = iso31662.find(
          (sub) => sub.code === `${country}-${region}`
        );
        subdivisionInfo = Object.entries(countryInfo.sub).find(
          (entry) =>
            normalizeStr(entry[1].name) === normalizeStr(subdivision.name)
        );
      }
      if (subdivisionInfo) {
        const completeSubdivisionInfo = iso3166.subdivision(subdivisionInfo[0]);
        if (completeSubdivisionInfo)
          region = completeSubdivisionInfo.regionCode;
      }
    }

    return {
      country,
      region,
    };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Error extracting Country and Region info', error);

    return {
      country: '',
      region: '',
    };
  }
};

const getPostalCode = (context) => {
  try {
    return context.find((elem) => elem.id.startsWith('postcode'))?.text || '';
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Error extracting Postal Code info', error);

    return '';
  }
};

export const buildAddress = async (result) => {
  let addressLine1 = (result.address ? `${result.address} ` : '') + result.text;

  if (result.place_name.startsWith(result.text)) {
    addressLine1 = result.text + (result.address ? ` ${result.address}` : '');
  }

  const { country, region } = await getCountryAndRegion(result.context);

  return {
    addressLine1,
    city: getCity(result.context),
    country,
    postalCode: getPostalCode(result.context),
    stateOrProvince: region,
  };
};
